// src/screens/Rides.jsx
import "bootstrap-icons/font/bootstrap-icons.css";
import { useEffect, useMemo, useRef, useState } from "react";
import "../css/Rides.css";

import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

// ✅ ENV ONLY (no blank fallback)
const RIDES_API = (process.env.REACT_APP_RIDES_API_URL || "").trim();

/* ---------------- helpers ---------------- */
const unique = (arr) =>
  Array.from(new Set((arr || []).filter(Boolean).map((s) => String(s).trim())));
const pad2 = (n) => String(n).padStart(2, "0");
const norm = (s) => String(s || "").trim().toLowerCase();

const fmtDateTime = (d) => {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d.replace(" ", "T")) : d;
  if (!dt || isNaN(dt.getTime())) return "—";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}, ${pad2(
    dt.getHours()
  )}:${pad2(dt.getMinutes())}:${pad2(dt.getSeconds())}`;
};

const currency = (n) =>
  `BTN. ${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const toNum = (v) =>
  v == null ? null : typeof v === "number" ? v : Number.parseFloat(v);

const toDate = (v) => {
  if (!v) return null;
  const s = String(v).replace(" ", "T");
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const sum = (...vals) =>
  vals.reduce((acc, v) => (Number.isFinite(toNum(v)) ? acc + Number(v) : acc), 0);

function toKm(distance_maybe_m) {
  const n = toNum(distance_maybe_m);
  if (!Number.isFinite(n)) return null;
  return n > 100 ? Math.round((n / 1000) * 10) / 10 : Math.round(n * 10) / 10;
}

/* ---------- detail helpers (HTML + PDF) ---------- */
function toLocalDT(d) {
  if (!d) return "—";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (!dt || isNaN(dt.getTime())) return "—";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Thimphu",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
      .format(dt)
      .replace(",", "");
  } catch {
    return fmtDateTime(d);
  }
}

/* ===================== PDF TEXT FIXERS (SAFE) ===================== */
function fixSpacedLetters(s) {
  const str = String(s ?? "");
  return str.replace(/([A-Za-z0-9])\s(?=[A-Za-z0-9])/g, "$1");
}

function pdfSafeText(s) {
  if (s == null) return "—";
  let x = String(s);

  // normalize
  x = x.replace(/\u00A0/g, " "); // NBSP
  x = x.replace(/[\u200B-\u200D\uFEFF]/g, ""); // zero-width
  x = x.replace(/[^\u0020-\u007E\n]/g, ""); // printable ASCII + newline

  x = x.replace(/[ \t]+/g, " ").replace(/\s*\n\s*/g, "\n").trim();
  x = fixSpacedLetters(x);
  x = x.replace(/[ ]{2,}/g, " ").trim();

  return x || "—";
}

/* ===================== DETAILS EXTRACT ===================== */
function extractDetails(row) {
  const { raw } = row || {};
  const isNew = raw && raw.driver_details && raw.ride_details && raw.fare_details;

  if (isNew) {
    const dd = raw.driver_details || {};
    const pd = raw.passenger_details || {};
    const rd = raw.ride_details || {};
    const fd = raw.fare_details || {};

    return {
      id: rd.ride_id ?? row.id ?? "—",
      driver_name:
        (dd.driver_name || "").trim() ||
        (dd.driver_id != null ? `#${dd.driver_id}` : "—"),
      driver_phone: dd.driver_phone || "—",
      driver_id: dd.driver_id ?? "—",

      rider_name:
        (pd.passenger_name || "").trim() ||
        (pd.passenger_id != null ? `#${pd.passenger_id}` : "—"),
      rider_phone: pd.passenger_phone || "—",
      rider_id: pd.passenger_id ?? "—",

      service_type: rd.service_type || "—",
      trip_type: rd.trip_type || "—",

      requested_at: rd.requested_at ? toLocalDT(rd.requested_at) : "—",
      accepted_at: rd.accepted_at ? toLocalDT(rd.accepted_at) : "—",
      arrived_pickup_at: rd.arrived_pickup_at ? toLocalDT(rd.arrived_pickup_at) : "—",
      started_at: rd.started_at ? toLocalDT(rd.started_at) : "—",
      completed_at: rd.completed_at ? toLocalDT(rd.completed_at) : "—",

      pickup_place: rd.pickup_place || row.pickup || "—",
      dropoff_place: rd.dropoff_place || row.drop || "—",
      distance_m: rd.distance_m ?? null,
      duration_s: rd.duration_s ?? null,

      rating: rd.driver_ratings?.rating ?? row.rating ?? null,
      rating_note: rd.driver_ratings?.comments || "",

      currency: fd.currency || "BTN",
      base_gross_nu: fd.base_gross_nu ?? null,
      time_gross_nu: fd.time_gross_nu ?? null,
      tips_nu: fd.tips_nu ?? null,
      platform_fee_nu: fd.platform_fee_nu ?? null,
      tax_nu: fd.tax_nu ?? null,
      driver_take_home_nu: fd.driver_take_home_nu ?? null,
      payment_method: fd.payment_method || row.paymentMethod || "—",
    };
  }

  // legacy fallback
  return {
    id: row.id ?? "—",
    driver_name: row.driver || "—",
    driver_phone: row.driverPhone || "—",
    driver_id: "—",
    rider_name: row.rider || "—",
    rider_phone: row.riderPhone || "—",
    rider_id: "—",
    service_type: row.rideType || "—",
    trip_type: "—",
    requested_at: "—",
    accepted_at: "—",
    arrived_pickup_at: "—",
    started_at: row.date || "—",
    completed_at: norm(row.status) === "completed" ? row.date : "—",
    pickup_place: row.pickup_place || row.pickup || "—",
    dropoff_place: row.dropoff_place || row.drop || "—",
    distance_m: row.km != null ? Math.round(Number(row.km) * 1000) : null,
    duration_s: null,
    rating: row.rating ?? null,
    rating_note: "",
    currency: "BTN",
    base_gross_nu: null,
    time_gross_nu: null,
    tips_nu: null,
    platform_fee_nu: null,
    tax_nu: null,
    driver_take_home_nu: row.fare ?? null,
    payment_method: row.paymentMethod || "—",
  };
}

function buildDetailsHTML(d) {
  const km = d.distance_m != null ? toKm(d.distance_m) : null;

  return `
    <div style="text-align:left; max-height:70vh; overflow:auto;">
      <div style="margin-bottom:10px;">
        <span style="display:inline-block; padding:.2rem .6rem; background:#e9f5ff; color:#0d6efd; border-radius:999px; font-weight:600;">
          Ride #${d.id ?? "—"}
        </span>
      </div>

      <h4 style="margin:10px 0 6px;">Driver</h4>
      <table style="width:100%; border-collapse:collapse;">
        <tbody>
          <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Name</td><td>${d.driver_name || "—"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Phone</td><td>${d.driver_phone || "—"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">ID</td><td>${d.driver_id ?? "—"}</td></tr>
        </tbody>
      </table>

      <h4 style="margin:14px 0 6px;">Passenger</h4>
      <table style="width:100%; border-collapse:collapse;">
        <tbody>
          <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Name</td><td>${d.rider_name || "—"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Phone</td><td>${d.rider_phone || "—"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">ID</td><td>${d.rider_id ?? "—"}</td></tr>
        </tbody>
      </table>

      <h4 style="margin:14px 0 6px;">Ride</h4>
      <table style="width:100%; border-collapse:collapse;">
        <tbody>
          <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Service</td><td>${d.service_type || "—"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Trip Type</td><td>${d.trip_type || "—"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Requested</td><td>${d.requested_at}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Accepted</td><td>${d.accepted_at}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Arrived Pickup</td><td>${d.arrived_pickup_at}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Started</td><td>${d.started_at}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Completed</td><td>${d.completed_at}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Pickup</td><td>${d.pickup_place || "—"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Dropoff</td><td>${d.dropoff_place || "—"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Distance</td><td>${km == null ? "—" : km + " km"}</td></tr>
          <tr><td style="padding:6px 0; color:#6b7280;">Duration (s)</td><td>${d.duration_s ?? "—"}</td></tr>
          ${d.rating != null ? `<tr><td style="padding:6px 0; color:#6b7280;">Driver Rating</td><td>${d.rating} / 5</td></tr>` : ""}
          ${d.rating_note ? `<tr><td style="padding:6px 0; color:#6b7280;">Rating Note</td><td>${d.rating_note}</td></tr>` : ""}
        </tbody>
      </table>

      <h4 style="margin:14px 0 6px;">Fare</h4>
      <table style="width:100%; border-collapse:collapse;">
        <tbody>
          <tr><td style="padding:6px 0; width:160px; color:#6b7280;">Currency</td><td>${d.currency || "BTN"}</td></tr>
          ${d.base_gross_nu != null ? `<tr><td style="padding:6px 0; color:#6b7280;">Base Gross</td><td>${currency(d.base_gross_nu)}</td></tr>` : ""}
          ${d.time_gross_nu != null ? `<tr><td style="padding:6px 0; color:#6b7280;">Time Gross</td><td>${currency(d.time_gross_nu)}</td></tr>` : ""}
          ${d.tips_nu != null ? `<tr><td style="padding:6px 0; color:#6b7280;">Tips</td><td>${currency(d.tips_nu)}</td></tr>` : ""}
          ${d.platform_fee_nu != null ? `<tr><td style="padding:6px 0; color:#6b7280;">Platform Fee</td><td>${currency(d.platform_fee_nu)}</td></tr>` : ""}
          ${d.tax_nu != null ? `<tr><td style="padding:6px 0; color:#6b7280;">Tax</td><td>${currency(d.tax_nu)}</td></tr>` : ""}
          ${d.driver_take_home_nu != null ? `<tr><td style="padding:6px 0; color:#6b7280;">Driver Take-Home</td><td>${currency(d.driver_take_home_nu)}</td></tr>` : ""}
          <tr><td style="padding:6px 0; color:#6b7280;">Payment Method</td><td>${d.payment_method || "—"}</td></tr>
        </tbody>
      </table>
    </div>
  `;
}

/* ===================== PDF EXPORT ===================== */
function exportRidePDF(d) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const tableW = pageW - marginX * 2;

  const col0 = 150;
  const col1 = Math.max(200, tableW - col0);

  const colStyles = {
    0: { cellWidth: col0, fontStyle: "bold" },
    1: { cellWidth: col1 },
  };

  const row = (k, v) => [pdfSafeText(k), pdfSafeText(v)];

  doc.setFillColor(13, 110, 253);
  doc.rect(0, 0, pageW, 64, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(`Ride #${pdfSafeText(d.id)} — Details`, marginX, 38);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Exported: ${fmtDateTime(new Date())}`, marginX, 54);
  doc.setTextColor(20, 20, 20);

  const sectionHeadStyles = {
    fillColor: [13, 110, 253],
    textColor: 255,
    fontStyle: "bold",
  };

  const baseStyles = {
    font: "helvetica",
    fontSize: 10,
    cellPadding: 6,
    overflow: "linebreak",
    valign: "top",
  };

  const km = d.distance_m != null ? toKm(d.distance_m) : null;

  autoTable(doc, {
    startY: 84,
    margin: { left: marginX, right: marginX },
    head: [[pdfSafeText("Driver"), " "]],
    body: [row("Name", d.driver_name), row("Phone", d.driver_phone), row("ID", d.driver_id)],
    theme: "grid",
    headStyles: sectionHeadStyles,
    styles: baseStyles,
    columnStyles: colStyles,
  });

  const y1 = (doc.lastAutoTable?.finalY || 84) + 12;

  autoTable(doc, {
    startY: y1,
    margin: { left: marginX, right: marginX },
    head: [[pdfSafeText("Passenger"), " "]],
    body: [row("Name", d.rider_name), row("Phone", d.rider_phone), row("ID", d.rider_id)],
    theme: "grid",
    headStyles: sectionHeadStyles,
    styles: baseStyles,
    columnStyles: colStyles,
  });

  const y2 = (doc.lastAutoTable?.finalY || y1) + 12;

  autoTable(doc, {
    startY: y2,
    margin: { left: marginX, right: marginX },
    head: [[pdfSafeText("Ride"), " "]],
    body: [
      row("Service", d.service_type),
      row("Trip Type", d.trip_type),
      row("Requested", d.requested_at),
      row("Accepted", d.accepted_at),
      row("Arrived Pickup", d.arrived_pickup_at),
      row("Started", d.started_at),
      row("Completed", d.completed_at),
      row("Pickup", d.pickup_place),
      row("Dropoff", d.dropoff_place),
      row("Distance", km == null ? "—" : `${km} km`),
      row("Duration (s)", d.duration_s ?? "—"),
      ...(d.rating != null ? [row("Driver Rating", `${d.rating} / 5`)] : []),
      ...(d.rating_note ? [row("Rating Note", d.rating_note)] : []),
    ],
    theme: "grid",
    headStyles: sectionHeadStyles,
    styles: baseStyles,
    columnStyles: colStyles,
  });

  const y3 = (doc.lastAutoTable?.finalY || y2) + 12;

  autoTable(doc, {
    startY: y3,
    margin: { left: marginX, right: marginX },
    head: [[pdfSafeText("Fare"), " "]],
    body: [
      row("Currency", d.currency || "BTN"),
      ...(d.base_gross_nu != null ? [row("Base Gross", currency(d.base_gross_nu))] : []),
      ...(d.time_gross_nu != null ? [row("Time Gross", currency(d.time_gross_nu))] : []),
      ...(d.tips_nu != null ? [row("Tips", currency(d.tips_nu))] : []),
      ...(d.platform_fee_nu != null ? [row("Platform Fee", currency(d.platform_fee_nu))] : []),
      ...(d.tax_nu != null ? [row("Tax", currency(d.tax_nu))] : []),
      ...(d.driver_take_home_nu != null
        ? [row("Driver Take-Home", currency(d.driver_take_home_nu))]
        : []),
      row("Payment Method", d.payment_method || "—"),
    ],
    theme: "grid",
    headStyles: sectionHeadStyles,
    styles: baseStyles,
    columnStyles: colStyles,
  });

  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(
    "Note: Unicode (Dzongkha/Tibetan) may not render in PDF unless a Unicode font is embedded.",
    marginX,
    pageH - 20
  );

  doc.save(`ride_${String(d.id || "detail").replace(/[^\w-]+/g, "_")}.pdf`);
}

function showRideDetailModal(row) {
  const d = extractDetails(row);
  const html = buildDetailsHTML(d);

  Swal.fire({
    title: `Ride Detail`,
    html,
    width: 860,
    showCloseButton: true,
    confirmButtonText: "Close",
    showDenyButton: true,
    denyButtonText: "Export PDF",
    focusConfirm: false,
  }).then((res) => {
    if (res.isDenied) {
      try {
        exportRidePDF(d);
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "PDF export failed",
          text: e?.message || "Could not generate PDF.",
        });
      }
    }
  });
}

/** NEW payouts API normalizer */
function normalizeFromNewAPI(row) {
  const dd = row.driver_details || {};
  const pd = row.passenger_details || {};
  const rd = row.ride_details || {};
  const fd = row.fare_details || {};

  const id = rd.ride_id ?? `R-${Math.random().toString(36).slice(2, 7)}`;
  const driver = dd.driver_name?.trim() || (dd.driver_id != null ? `#${dd.driver_id}` : "—");
  const rider =
    pd.passenger_name?.trim() || (pd.passenger_id != null ? `#${pd.passenger_id}` : "—");

  const driverPhone = dd.driver_phone || "—";
  const riderPhone = pd.passenger_phone || "—";

  const rideType = rd.service_type || rd.trip_type || "—";

  const dateRaw = rd.completed_at || rd.started_at || rd.accepted_at || rd.requested_at || null;
  const date = fmtDateTime(dateRaw);

  const km = toKm(rd.distance_m);
  const fareGross = sum(fd.driver_take_home_nu, fd.time_gross_nu, fd.tips_nu);
  const status = rd.completed_at ? "Completed" : "In-Progress";
  const rating = toNum(rd.driver_ratings?.rating);
  const pickup_place = rd.pickup_place || "";
  const dropoff_place = rd.dropoff_place || "";
  const paymentMethod = fd.payment_method || "Unknown";

  return {
    id,
    driver,
    rider,
    driverPhone,
    riderPhone,
    rideType,
    date,
    dateRaw,
    km,
    fare: fareGross,
    status,
    rating,
    pickup: pickup_place || "—",
    drop: dropoff_place || "—",
    pickup_place,
    dropoff_place,
    paymentMethod,
    raw: row,
  };
}

/** Legacy normalizer */
function normalizeRide(row, i) {
  if (row && row.driver_details && row.ride_details && row.fare_details) {
    return normalizeFromNewAPI(row);
  }

  const id =
    row.id ?? row.ride_id ?? row.rideId ?? row.code ?? `R-${String(i + 1).padStart(3, "0")}`;

  const driver =
    row.driver?.name ||
    row.driver_name ||
    row.driverName ||
    (typeof row.driver === "string"
      ? row.driver
      : row.driver_id != null
      ? `#${row.driver_id}`
      : "—");

  const rider =
    row.rider?.name ||
    row.passenger_name ||
    row.passengerName ||
    (typeof row.rider === "string"
      ? row.rider
      : row.passenger_id != null
      ? `#${row.passenger_id}`
      : "—");

  const driverPhone = row.driver?.phone || row.driver_phone || row.driverPhone || "—";
  const riderPhone = row.rider?.phone || row.passenger_phone || row.passengerPhone || "—";
  const rideType =
    row.ride_type ||
    row.type ||
    row.vehicle_type ||
    (typeof row.vehicle === "string" ? row.vehicle : "—");

  const dateRaw =
    row.completed_at ??
    row.started_at ??
    row.start_time ??
    row.created_at ??
    row.createdAt ??
    row.date ??
    "";

  const date = fmtDateTime(dateRaw);

  const km =
    row.distance_km ??
    row.km ??
    (typeof row.distance_m === "number"
      ? Math.round((row.distance_m / 1000) * 10) / 10
      : typeof row.distance_m === "string"
      ? Math.round((Number(row.distance_m) / 1000) * 10) / 10
      : null);

  const fare = row.fare_amount ?? row.amount ?? row.price ?? row.fare ?? null;

  const s = norm(row.status ?? row.state ?? row.ride_status ?? "");
  let status = "In-Progress";
  if (["completed", "complete", "done"].includes(s)) status = "Completed";
  else if (["cancelled", "canceled"].includes(s)) status = "Cancelled";
  else if (["accepted", "accepted_by_driver", "assigned"].includes(s)) status = "Accepted";
  else status = row.status || "In-Progress";

  const pickup_place = row.pickup_place ?? "";
  const dropoff_place = row.dropoff_place ?? "";

  const pickup = pickup_place || row.pickup || row.origin || "—";
  const drop = dropoff_place || row.dropoff || row.destination || "—";

  const rating =
    row.rating ??
    row.average_rating ??
    row.avg_rating ??
    row.stars ??
    (typeof row.rating_value === "number" ? row.rating_value : null);

  const paymentMethod =
    row.payment_method ??
    row.paymentMode ??
    row.payment_mode ??
    row.payment ??
    row.pay_method ??
    null;

  return {
    id,
    driver,
    rider,
    driverPhone,
    riderPhone,
    rideType,
    date,
    dateRaw,
    km,
    fare,
    status,
    rating,
    pickup,
    drop,
    pickup_place,
    dropoff_place,
    paymentMethod,
    raw: row,
  };
}

function renderRating(rating) {
  if (rating == null || isNaN(rating)) return "—";
  const full = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.25 && rating % 1 < 0.75;
  const empty = 5 - full - (hasHalf ? 1 : 0);
  return (
    <span className="rating-cell">
      <span className="rating-stars">
        {Array.from({ length: full }).map((_, i) => (
          <i key={`f${i}`} className="bi bi-star-fill" />
        ))}
        {hasHalf && <i className="bi bi-star-half" />}
        {Array.from({ length: empty }).map((_, i) => (
          <i key={`e${i}`} className="bi bi-star" />
        ))}
      </span>
      <span className="rating-number">({Number(rating).toFixed(1)})</span>
    </span>
  );
}

export default function Rides() {
  useIdleLogout();

  const [isDark, setIsDark] = useState(document.body.classList.contains("dark-mode"));
  useEffect(() => {
    const mo = new MutationObserver(() =>
      setIsDark(document.body.classList.contains("dark-mode"))
    );
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const ROWS_PER_PAGE = 20;
  const [page, setPage] = useState(1);
  const keyRef = useRef("");

  const token = localStorage.getItem("access_token") || "";

  async function fetchRides() {
    if (!RIDES_API) {
      setLoading(false);
      setRides([]);
      setErr("Missing API config: Please set REACT_APP_RIDES_API_URL in your .env and restart.");
      return;
    }

    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("startDate", dateFrom);
      if (dateTo) params.set("endDate", dateTo);
      if (q.trim()) params.set("q", q.trim());
      const url = params.toString() ? `${RIDES_API}?${params}` : RIDES_API;

      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText} ${t.slice(0, 300)}`);
      }

      const data = await res.json();
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.items)
        ? data.items
        : [];

      setRides(items.map(normalizeRide));
    } catch (e) {
      setErr(e.message || "Failed to load rides.");
      setRides([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const k = `${dateFrom}|${dateTo}|${q}`;
    if (keyRef.current !== k) {
      keyRef.current = k;
      setPage(1);
    }
    const t = setTimeout(fetchRides, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, q]);

  const filtered = useMemo(() => {
    const qx = q.trim().toLowerCase();
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

    return rides.filter((r) => {
      const hay = `${r.id} ${r.driver} ${r.rider} ${r.driverPhone} ${r.riderPhone} ${r.rideType} ${r.status} ${r.pickup} ${r.drop}`.toLowerCase();
      const okText = !qx || hay.includes(qx);

      const d = toDate(r.dateRaw);
      const okFrom = !from || (d && d >= from);
      const okTo = !to || (d && d <= to);

      return okText && okFrom && okTo;
    });
  }, [rides, q, dateFrom, dateTo]);

  const tableAll = useMemo(
    () => filtered.filter((r) => ["completed", "cancelled"].includes(norm(r.status))),
    [filtered]
  );

  const pageCount = Math.max(1, Math.ceil(tableAll.length / ROWS_PER_PAGE));
  const pageSafe = Math.min(Math.max(page, 1), pageCount);

  const tableRows = useMemo(() => {
    const start = (pageSafe - 1) * ROWS_PER_PAGE;
    return tableAll.slice(start, start + ROWS_PER_PAGE);
  }, [tableAll, pageSafe]);

  const totalOrders = filtered.length;
  const completed = useMemo(
    () => tableAll.filter((r) => norm(r.status) === "completed").length,
    [tableAll]
  );
  const cancelled = useMemo(
    () => tableAll.filter((r) => norm(r.status) === "cancelled").length,
    [tableAll]
  );
  const inProgress = totalOrders - completed - cancelled;

  const rideTypeLabels = useMemo(() => {
    const labels = unique(filtered.map((r) => r.rideType));
    return labels.length ? labels : ["No Data"];
  }, [filtered]);

  const rideTypesData = useMemo(() => {
    const labels = rideTypeLabels;
    const counts =
      labels[0] === "No Data"
        ? [1]
        : labels.map(
            (lbl) =>
              filtered.filter(
                (r) => String(r.rideType).trim().toLowerCase() === lbl.toLowerCase()
              ).length
          );

    return {
      labels,
      datasets: [
        {
          data: counts,
          backgroundColor: [
            "#0d6efd",
            "#20c997",
            "#fd7e14",
            "#6610f2",
            "#198754",
            "#dc3545",
            "#0dcaf0",
            "#6f42c1",
            "#ffc107",
            "#1982c4",
          ].slice(0, labels.length),
        },
      ],
    };
  }, [rideTypeLabels, filtered]);

  const completedForChart = useMemo(
    () => filtered.filter((r) => norm(r.status) === "completed"),
    [filtered]
  );

  const paymentLabels = useMemo(() => {
    const raw = completedForChart.map((r) => r.paymentMethod || "Unknown");
    const labels = unique(raw);
    return labels.length ? labels : ["No Data"];
  }, [completedForChart]);

  const paymentData = useMemo(() => {
    const labels = paymentLabels;
    const counts =
      labels[0] === "No Data"
        ? [1]
        : labels.map(
            (lbl) =>
              completedForChart.filter(
                (r) =>
                  String(r.paymentMethod || "Unknown").trim().toLowerCase() === lbl.toLowerCase()
              ).length
          );

    return {
      labels,
      datasets: [
        {
          data: counts,
          backgroundColor: labels.map(
            (_, i) =>
              [
                "#0d6efd",
                "#198754",
                "#dc3545",
                "#ffc107",
                "#20c997",
                "#6f42c1",
                "#fd7e14",
                "#0dcaf0",
                "#6610f2",
                "#1982c4",
              ][i % 10]
          ),
        },
      ],
    };
  }, [paymentLabels, completedForChart]);

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      cutout: "60%",
      layout: { padding: 10 },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 20,
            padding: 14,
            color: isDark ? "#e6e6e6" : "#111827",
          },
        },
        tooltip: {
          backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
          titleColor: isDark ? "#F3F4F6" : "#111827",
          bodyColor: isDark ? "#F3F4F6" : "#111827",
          borderColor: isDark ? "#374151" : "#D1D5DB",
          borderWidth: 1,
          callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}` },
        },
      },
    }),
    [isDark]
  );

  const handleExportXLSX = async () => {
    try {
      const XLSXModule = await import("xlsx");
      const XLSX = XLSXModule.default || XLSXModule;
      const wb = XLSX.utils.book_new();
      const data = [];
      const range =
        dateFrom || dateTo ? `(${dateFrom || "—"} — ${dateTo || "—"})` : "(All Dates)";

      data.push([`Ride Logs ${range}`]);
      data.push([`Exported: ${fmtDateTime(new Date())}`]);
      data.push([]);
      data.push([
        "Sl.No",
        "Ride ID",
        "Driver Name",
        "Driver Phone",
        "Passenger Name",
        "Passenger Phone",
        "Ride Type",
        "Pickup",
        "Drop-off",
        "Date & Time",
        "Distance (km)",
        "Fare (BTN.)",
        "Status",
        "Rating",
        "Payment Method",
      ]);

      filtered.forEach((r, i) =>
        data.push([
          i + 1,
          r.id,
          r.driver,
          r.driverPhone,
          r.rider,
          r.riderPhone,
          r.rideType,
          r.pickup,
          r.drop,
          r.date,
          r.km ?? "",
          r.fare == null ? "" : Number(r.fare),
          r.status,
          r.rating ?? "",
          r.paymentMethod || "",
        ])
      );

      const ws = XLSX.utils.aoa_to_sheet(data);
      ws["!cols"] = [
        { wch: 8 },
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 16 },
        { wch: 30 },
        { wch: 30 },
        { wch: 20 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 10 },
        { wch: 16 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Ride Logs");
      XLSX.writeFile(wb, `rides_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      alert("To export Excel, please install:\n\nnpm i xlsx");
    }
  };

  // Pagination removed from UI (kept page state for slicing)

  return (
    <>
      {err && (
        <div className="alert alert-danger" role="alert">
          {err}
        </div>
      )}

      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 orders-metrics">
        {[
          { label: "Total Rides", value: totalOrders },
          { label: "In-Progress", value: inProgress },
          { label: "Completed", value: completed },
          { label: "Cancelled", value: cancelled },
        ].map((m, i) => (
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Ride Types</div>
            <div className="card-body">
              <Doughnut data={rideTypesData} options={doughnutOptions} />
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Payment Method Breakdown (Completed Only)</div>
            <div className="card-body">
              <Doughnut data={paymentData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3 search-filter-card">
        <div className="card-body searchbar-row-inline">
          <span className="search-icon" aria-hidden="true">
            <i className="bi bi-search" />
          </span>

          <div className="search-wrap">
            <input
              type="text"
              className="form-control search-pill"
              placeholder="Search by ID, driver, rider, phone, ride type, status, pickup, drop-off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search rides"
            />
          </div>

          <div className="date-range">
            <input
              type="date"
              className="date-pill"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Start date"
            />
            <span className="range-sep">to</span>
            <input
              type="date"
              className="date-pill"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="End date"
            />
          </div>

          <div className="exports ms-auto d-flex gap-2">
            <button className="btn btn-success" onClick={handleExportXLSX}>
              Export Excel
            </button>
          </div>
        </div>
      </div>

      <div className="orders-table-card card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Completed & Cancelled Rides</h5>
          
        </div>

        <div className="table-responsive">
          {/* ✅ FIXED COLUMN WIDTHS (colgroup) */}
          <table className="table table-hover align-middle table-pro table-rides-fixed">
            <colgroup>
              <col style={{ width: "80px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "170px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "170px" }} />
              <col style={{ width: "170px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "280px" }} />
              <col style={{ width: "280px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "130px" }} />
            </colgroup>

            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Ride ID</th>
                <th>Driver Name</th>
                <th>Driver Phone</th>
                <th>Passenger Name</th>
                <th>Passenger Phone</th>
                <th>Ride Type</th>
                <th>Pickup</th>
                <th>Drop-off</th>
                <th>Date &amp; Time</th>
                <th>Distance (km)</th>
                <th>Fare (BTN.)</th>
                <th>Status</th>
                <th>Rating</th>
                <th style={{ textAlign: "center" }}>Details</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={15} className="py-4 text-center text-muted">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading &&
                tableRows.map((r, i) => {
                  const idx = (pageSafe - 1) * ROWS_PER_PAGE + i;
                  return (
                    <tr key={`${r.id}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td className="mono">{r.id}</td>
                      <td className="cell-ellipsis" title={r.driver || ""}>
                        {r.driver}
                      </td>
                      <td className="mono">{r.driverPhone}</td>
                      <td className="cell-ellipsis" title={r.rider || ""}>
                        {r.rider}
                      </td>
                      <td className="mono">{r.riderPhone}</td>
                      <td className="cell-ellipsis" title={r.rideType || ""}>
                        {r.rideType}
                      </td>

                      <td className="cell-ellipsis" title={(r.pickup_place || r.pickup) || ""}>
                        {r.pickup_place || r.pickup}
                      </td>
                      <td className="cell-ellipsis" title={(r.dropoff_place || r.drop) || ""}>
                        {r.dropoff_place || r.drop}
                      </td>

                      <td className="mono">{r.date || "—"}</td>
                      <td className="mono">{r.km ?? "—"}</td>
                      <td className="mono">{r.fare == null ? "—" : currency(r.fare)}</td>
                      <td>{r.status}</td>
                      <td>{renderRating(r.rating)}</td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                          title={`View details for Ride #${r.id ?? ""}`}
                          onClick={() => showRideDetailModal(r)}
                        >
                          <i className="bi bi-eye" />
                          <span className="d-none d-sm-inline">View</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}

              {!loading && tableRows.length === 0 && (
                <tr>
                  <td colSpan={15} className="text-center py-3 text-muted">
                    No completed or cancelled rides.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      <footer className="dashboard-footer mt-auto">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved
        </small>
      </footer>
    </>
  );
}
